// src/components/hub/CategoryDropdown.jsx
import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Lock, BookOpen,
         Sparkles, Globe, TrendingUp, BookMarked, Smile } from 'lucide-react';

// ── Configuración visual de cada filtro ──────────────
const CATEGORIES = [
  {
    id: 'library',
    label: 'Ver Todo',
    emoji: '📚',
    color: 'bg-indigo-500',
    light: 'bg-indigo-50 border-indigo-200 text-indigo-700',
    active: 'bg-indigo-500 text-white border-indigo-600',
  },
  {
    id: 'cat-verbs',
    label: 'Verbos',
    emoji: '📝',
    color: 'bg-violet-500',
    light: 'bg-violet-50 border-violet-200 text-violet-700',
    active: 'bg-violet-500 text-white border-violet-600',
  },
  {
    id: 'cat-slang',
    label: 'Slang',
    emoji: '😎',
    color: 'bg-pink-500',
    light: 'bg-pink-50 border-pink-200 text-pink-700',
    active: 'bg-pink-500 text-white border-pink-600',
  },
  {
    id: 'cat-general',
    label: 'General',
    emoji: '🌍',
    color: 'bg-teal-500',
    light: 'bg-teal-50 border-teal-200 text-teal-700',
    active: 'bg-teal-500 text-white border-teal-600',
  },
  {
    id: 'cat-phrasal',
    label: 'Phrasal Verbs',
    emoji: '📈',
    color: 'bg-orange-500',
    light: 'bg-orange-50 border-orange-200 text-orange-700',
    active: 'bg-orange-500 text-white border-orange-600',
  },
  {
    id: 'cat-idioms',
    label: 'Idioms',
    emoji: '📒',
    color: 'bg-cyan-500',
    light: 'bg-cyan-50 border-cyan-200 text-cyan-700',
    active: 'bg-cyan-500 text-white border-cyan-600',
  },
];

const MODULE_COLORS = [
  'from-indigo-400 to-indigo-600',
  'from-violet-400 to-violet-600',
  'from-purple-400 to-purple-600',
  'from-fuchsia-400 to-fuchsia-600',
  'from-pink-400 to-pink-600',
  'from-rose-400 to-rose-600',
  'from-orange-400 to-orange-600',
  'from-amber-400 to-amber-600',
  'from-teal-400 to-teal-600',
  'from-cyan-400 to-cyan-600',
];

export default function CategoryDropdown({
  selectedFilter,
  onFilterChange,
  isModuleLocked,
  onLockedModuleClick,
}) {
  const [showModules, setShowModules] = useState(false);

  const handleCategoryClick = (id) => onFilterChange(id);

  const handleModuleClick = (num) => {
  if (isModuleLocked(num)) {
    onLockedModuleClick?.(num);
  } else {
    onFilterChange(String(num)); // ← convierte a string igual que el select ✅
    setShowModules(false);
  }
};

  const activeCategory = CATEGORIES.find(c => c.id === selectedFilter);
  const activeModule   = typeof selectedFilter === 'number' ? selectedFilter : null;

  return (
    <div className="px-4 mb-4 space-y-3">

      {/* ── CHIPS DE CATEGORÍAS ─────────────────────── */}
      <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
        {CATEGORIES.map(cat => {
          const isActive = selectedFilter === cat.id;
          return (
            <button
              key={cat.id}
              onClick={() => handleCategoryClick(cat.id)}
              className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-2xl border-2 font-black text-xs transition-all active:scale-95
                ${isActive ? cat.active + ' shadow-md scale-[1.03]' : cat.light + ' hover:scale-[1.02]'}`}
            >
              <span>{cat.emoji}</span>
              {cat.label}
            </button>
          );
        })}
      </div>

      {/* ── BOTÓN DE MÓDULOS ────────────────────────── */}
      <button
        onClick={() => setShowModules(prev => !prev)}
        className={`w-full flex items-center justify-between px-4 py-3 rounded-2xl border-2 transition-all
          ${activeModule
            ? `bg-gradient-to-r ${MODULE_COLORS[(activeModule - 1) % 10]} text-white border-transparent shadow-md`
            : 'bg-white border-indigo-100 text-slate-700 hover:border-indigo-300'
          }`}
      >
        <div className="flex items-center gap-2">
          <span className="text-base">📖</span>
          <div className="text-left">
            <p className={`text-[10px] font-black uppercase tracking-widest
              ${activeModule ? 'text-white/70' : 'text-indigo-400'}`}>
              Módulos del curso
            </p>
            <p className="text-sm font-black">
              {activeModule ? `Módulo ${activeModule}` : 'Seleccionar módulo'}
            </p>
          </div>
        </div>
        {showModules
          ? <ChevronUp  size={18} className={activeModule ? 'text-white/80' : 'text-slate-400'} />
          : <ChevronDown size={18} className={activeModule ? 'text-white/80' : 'text-slate-400'} />
        }
      </button>

      {/* ── GRID DE MÓDULOS (expandible) ────────────── */}
      {showModules && (
        <div className="grid grid-cols-5 gap-2">
          {[...Array(10)].map((_, i) => {
            const num     = i + 1;
            const locked  = isModuleLocked(num);
            const isActive = selectedFilter === num;
            return (
              <button
                key={num}
                onClick={() => handleModuleClick(num)}
                className={`relative flex flex-col items-center justify-center py-3 rounded-2xl border-2 font-black text-sm transition-all active:scale-95
                  ${isActive
                    ? `bg-gradient-to-br ${MODULE_COLORS[i]} text-white border-transparent shadow-lg scale-[1.05]`
                    : locked
                    ? 'bg-slate-50 border-slate-100 text-slate-300'
                    : 'bg-white border-indigo-50 text-slate-700 hover:border-indigo-200 hover:scale-[1.02]'
                  }`}
              >
                {locked && (
                  <Lock size={10} className="absolute top-1.5 right-1.5 text-slate-300" />
                )}
                <span className="text-base">{locked ? '🔒' : '📖'}</span>
                <span className="text-xs font-black mt-0.5">{num}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* ── LABEL del filtro activo ──────────────────── */}
      <div className="flex items-center gap-2 px-1">
        <div className={`w-2 h-2 rounded-full ${
          activeCategory ? activeCategory.color : 'bg-indigo-500'
        }`} />
        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
          {activeModule
            ? `Mostrando Módulo ${activeModule}`
            : activeCategory
            ? `Mostrando: ${activeCategory.label}`
            : 'Toda la biblioteca'
          }
        </p>
      </div>

    </div>
  );
}
